import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
export function FAQ() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const faqs = [{
    question: 'Does RealtyZen ever send emails automatically?',
    answer: 'Never. Every email RealtyZen creates is saved to as a draft in Gmail. You always review and approve before sending, either by clicking Send in Gmail or using voice commands like "send," "edit," or "skip."'
  }, {
    question: 'How quickly does RealtyZen respond to new emails?',
    answer: 'RealtyZen detects new emails within 30 seconds and creates drafts within 2-3 minutes. This helps you stay within the crucial 5-minute response window that real estate leads expect, even when you\'re showing properties or in meetings.'
  }, {
    question: 'How secure is my client data?',
    answer: 'We use OAuth authentication (no password access) and follow enterprise security practices. We\'re implementing full encryption by launch. No human ever reviews your emails, and we never share data with third parties.'
  }, {
    question: 'Will the AI sound like me or like a robot?',
    answer: 'RealtyZen analyzes your last 500 sent emails to learn your communication style, tone, and common phrases. Most clients can\'t tell the difference between your writing and RealtyZen\'s drafts. You can also provide feedback to continuously improve the AI\'s accuracy.'
  }, {
    question: 'What email platforms work with RealtyZen?',
    answer: 'Currently Gmail and Google Workspace (G-Suite). Microsoft Outlook and other platforms are planned for 2025. You\'ll need to grant RealtyZen modify access to detect new emails and write access to create drafts in your account.'
  }, {
    question: 'How easy is the setup process?',
    answer: 'Setup takes less than 1 minute of your time. Just connect your Gmail account, let RealtyZen analyze your email style, and you\'re ready. Analyzing your email style takes about 30 minutes. The AI starts working immediately, getting smarter as you approve or edit its suggestions over the first few weeks.'
  }, {
    question: 'Can RealtyZen research properties and market data?',
    answer: 'Yes. RealtyZen can search the web for property details, comparable sales, neighborhood information, and market trends to include in your responses. This helps you provide comprehensive answers even when you\'re away from your desk.'
  }, {
    question: 'How does pricing work after the beta?',
    answer: 'Currently free during beta (no credit card required). After beta, pricing starts at $25/month for unlimited email drafts, with advanced features like voice commands and SMS integration in higher tiers. Beta users get grandfathered pricing and early access to new features.'
  }, {
    question: 'How does RealtyZen compare to hiring a virtual assistant?',
    answer: 'RealtyZen costs significantly less than a VA ($25/month vs $800+/month), works 24/7 without breaks, and knows your writing style intimately. Unlike a VA, there\'s no training time, no confidentiality concerns, and responses are available within minutes, not hours.'
  }];
  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="faq" className="relative py-16 sm:py-24" data-id="element-71">
      {/* Background with texture pattern */}
      <div className="absolute inset-0 bg-linen-100" data-id="element-72" />
      
      {/* Clean geometric grid pattern */}
      <div className="absolute inset-0" style={{
      backgroundImage: `
            linear-gradient(rgba(94, 130, 98, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(94, 130, 98, 0.03) 1px, transparent 1px)
          `,
      backgroundSize: '50px 50px'
    }} data-id="element-73" />
      
      <div className="container relative" data-id="element-74">
        {/* Header */}
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} data-id="element-75">
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl mb-4 text-slate-900 tracking-tight" data-id="element-76">
            Questions & Answers
          </h2>
          <p className="text-xl font-medium text-gray-700 max-w-2xl mx-auto leading-relaxed" data-id="element-77">
            Everything you need to know about RealtyZen.
          </p>
        </motion.div>

        {/* FAQ Grid */}
        <motion.div className="max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-78">
          <div className="space-y-4" data-id="element-79">
            {faqs.map((faq, index) => {
            const isExpanded = expandedIndex === index;
            return <motion.div key={index} variants={itemVariants} className="group" data-id="element-80">
                  <div className={cn("bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200/60", "shadow-sm hover:shadow-lg transition-all duration-300 ease-out", "hover:bg-white hover:border-sage-300/30", isExpanded && "shadow-lg bg-white border-sage-300/30")} data-id="element-81">
                    <button onClick={() => toggleFAQ(index)} className="w-full px-6 sm:px-8 py-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2 rounded-xl" aria-expanded={isExpanded} data-id="element-82">
                      <div className="flex items-start justify-between gap-4" data-id="element-83">
                        <div className="flex-1" data-id="element-84">
                          <h3 className="font-sans font-semibold text-lg sm:text-xl text-slate-900 leading-tight pr-4" data-id="element-85">
                            {faq.question}
                          </h3>
                        </div>
                        <motion.div animate={{
                      rotate: isExpanded ? 180 : 0
                    }} transition={{
                      duration: 0.2,
                      ease: "easeInOut"
                    }} className="flex-shrink-0 w-6 h-6 flex items-center justify-center mt-1" data-id="element-86">
                          <ChevronDown className={cn("w-5 h-5 transition-colors duration-200", isExpanded ? "text-sage-600" : "text-gray-500 group-hover:text-gray-700")} data-id="element-87" />
                        </motion.div>
                      </div>
                    </button>
                    
                    <AnimatePresence mode="wait" data-id="element-88">
                      {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: "auto",
                    opacity: 1,
                    transition: {
                      height: {
                        duration: 0.3,
                        ease: "easeOut"
                      },
                      opacity: {
                        duration: 0.2,
                        delay: 0.1
                      }
                    }
                  }} exit={{
                    height: 0,
                    opacity: 0,
                    transition: {
                      height: {
                        duration: 0.2,
                        ease: "easeIn"
                      },
                      opacity: {
                        duration: 0.1
                      }
                    }
                  }} className="overflow-hidden" data-id="element-89">
                          <div className="px-6 sm:px-8 pb-6" data-id="element-90">
                            <motion.p className="text-gray-700 leading-relaxed text-base" initial={{
                        y: 10,
                        opacity: 0
                      }} animate={{
                        y: 0,
                        opacity: 1
                      }} transition={{
                        duration: 0.3,
                        delay: 0.1
                      }} data-id="element-91">
                              {faq.answer}
                            </motion.p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
        
      </div>
    </section>;
}